import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowUpRight } from "lucide-react";
import { Seo } from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
  } catch {
    return "";
  }
};

const readTime = (mins) => (mins ? `${mins} min read` : "");

const CoverImage = ({ src, className = "", testId }) => (
  <div className={`overflow-hidden bg-[#0b1220] aspect-[3/2] ${className}`}>
    {src && (
      <img
        src={src}
        alt=""
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
        data-testid={testId}
      />
    )}
  </div>
);

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/articles`)
      .then((r) => {
        const list = Array.isArray(r.data) ? r.data : [];
        setPosts(list);
      })
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, []);

  const [featured, ...rest] = posts;

  return (
    <div className="bg-white min-h-screen" data-testid="insights-page">
      <Seo
        title="Insights · SideB — Field notes from an AI-First Consulting Studio"
        description="Field notes, systems teardowns, and operating playbooks from SideB — an Operating Partner and AI Solutions Management studio for growth-stage teams."
        keywords="AI systems, AI Solutions Management, RAG hallucinations, AIOps Partner, AI-first RevOps consulting, Process Automation Consulting, Payment gateway optimization"
        canonical="https://sideb.io/insights"
        schema={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "SideB Insights",
          url: "https://sideb.io/insights",
          description:
            "Field notes, systems teardowns, and operating playbooks from an AI-first consulting studio.",
          publisher: { "@type": "Organization", name: "SideB", url: "https://sideb.io" },
          blogPost: posts.map((p) => ({
            "@type": "BlogPosting",
            headline: p.title,
            url: `https://sideb.io/insights/${p.slug}`,
            datePublished: p.published_at,
            description: p.excerpt || p.subtitle,
            author: { "@type": "Organization", name: "SideB" },
          })),
        }}
      />

      {/* ---- DARK HEADER --------------------------------------------------- */}
      <section className="bg-[#0a1628] text-white border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 lg:py-24">
          <div className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-[0.18em] text-[#ac62ea] mb-5">
            <span className="w-1.5 h-1.5 rounded-full bg-[#ac62ea]" />
            Insights
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.035em] leading-[1.02] max-w-3xl">
            Field notes &amp; <span className="text-[#ac62ea]">systems teardowns.</span>
          </h1>
          <p className="mt-6 text-base text-white/65 max-w-2xl leading-relaxed">
            Operating playbooks, configuration critique, and short writeups from active SideB engagements. Published when we have something useful to say.
          </p>
        </div>
      </section>

      {loading && (
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 text-foreground/45 text-sm font-mono uppercase tracking-[0.14em]" data-testid="insights-loading">
          Loading insights…
        </div>
      )}

      {/* ---- FEATURED (dark) ---------------------------------------------- */}
      {!loading && featured && (
        <section className="bg-[#0a1628] text-white border-b border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <Link to={`/insights/${featured.slug}`} className="block group" data-testid="insight-featured">
              <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                <div className="lg:col-span-6 lg:order-2">
                  <CoverImage src={featured.cover_image} className="rounded-2xl border border-white/10" testId="insight-featured-cover" />
                </div>
                <div className="lg:col-span-6">
                  <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/45 mb-4">Featured</div>
                  <div className="flex items-center gap-3 text-xs mb-4 flex-wrap">
                    {featured.category && (
                      <>
                        <span className="text-[#ac62ea] font-mono uppercase tracking-[0.16em]">{featured.category}</span>
                        <span className="text-white/30">·</span>
                      </>
                    )}
                    {featured.read_minutes ? (
                      <>
                        <span className="text-white/50">{readTime(featured.read_minutes)}</span>
                        <span className="text-white/30">·</span>
                      </>
                    ) : null}
                    <span className="text-white/50">{formatDate(featured.published_at)}</span>
                  </div>
                  <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.05] group-hover:text-[#b967ff] transition-colors">
                    {featured.title}
                  </h2>
                  <p className="mt-5 text-white/70 leading-relaxed text-lg max-w-3xl">{featured.excerpt || featured.subtitle}</p>
                  <div className="mt-7 inline-flex items-center gap-1.5 text-sm font-medium text-[#ac62ea] group-hover:gap-2.5 transition-all">
                    Read insight
                    <ArrowUpRight className="h-4 w-4" />
                  </div>
                </div>
              </div>
            </Link>
          </div>
        </section>
      )}

      {/* ---- LIST (light) ------------------------------------------------- */}
      {!loading && rest.length > 0 && (
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <div className="flex items-baseline justify-between mb-10">
              <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-foreground/55">
                More insights
              </div>
              <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-foreground/40">
                {rest.length} {rest.length === 1 ? "insight" : "insights"}
              </div>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-x-12 lg:gap-y-14">
              {rest.map((p) => (
                <Link
                  key={p.slug}
                  to={`/insights/${p.slug}`}
                  className="group block"
                  data-testid={`insight-card-${p.slug}`}
                >
                  <CoverImage src={p.cover_image} className="rounded-xl mb-6" testId={`insight-card-cover-${p.slug}`} />
                  <div className="flex items-center gap-2 text-xs mb-3 flex-wrap">
                    {p.category && (
                      <span className="text-[#ac62ea] font-mono uppercase tracking-[0.14em]">{p.category}</span>
                    )}
                    {p.read_minutes ? (
                      <>
                        <span className="text-foreground/30">·</span>
                        <span className="text-foreground/55 font-mono uppercase tracking-[0.12em] text-[11px]">{readTime(p.read_minutes)}</span>
                      </>
                    ) : null}
                  </div>
                  <h3 className="font-display text-xl sm:text-2xl font-semibold tracking-[-0.02em] leading-snug text-foreground group-hover:text-[#6d28d9] transition-colors">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-sm text-foreground/65 leading-relaxed">{p.excerpt || p.subtitle}</p>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="text-xs text-foreground/45 font-mono uppercase tracking-[0.12em]">
                      {formatDate(p.published_at)}
                    </div>
                    <ArrowUpRight className="h-4 w-4 text-foreground/40 group-hover:text-[#ac62ea] group-hover:-translate-y-0.5 group-hover:translate-x-0.5 transition-all" />
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {!loading && posts.length === 0 && (
        <section className="bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <p className="text-sm text-foreground/45 font-mono uppercase tracking-[0.14em]">No insights yet. Check back soon.</p>
          </div>
        </section>
      )}
    </div>
  );
}
